/* Dialog de troca de papel de um operador (ADR-0709). locked durante a mutation (ADR-0216).
   O guard de lockout (demover o último super_admin ativo) é fail-closed no backend → 409
   last_super_admin, traduzido por `staffErrorMessage`. */
import type { StaffRole, StaffUserDto } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@planuze/ui';
import { useEffect, useState } from 'react';
import { useStaffManagementContent } from '../../content/sections/staff-management.js';
import { useUpdateStaffRole } from '../../hooks/use-staff.js';
import { staffErrorMessage } from './staff-error.js';
import { STAFF_ROLES, staffRoleDescription, staffRoleLabel } from './staff-labels.js';

interface Props {
  readonly staff: StaffUserDto;
  readonly onClose: () => void;
}

export function ChangeRoleDialog({ staff, onClose }: Props) {
  const copy = useStaffManagementContent();
  const update = useUpdateStaffRole();
  const [role, setRole] = useState<StaffRole>(staff.role);

  useEffect(() => {
    setRole(staff.role);
  }, [staff.role]);

  const submit = (): void => {
    update.mutate(
      { id: staff.id, role },
      {
        onSuccess: () => {
          toast.success(copy.changeRoleDialog.success);
          onClose();
        },
        onError: (err) => toast.error(staffErrorMessage(err, copy.errors)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(next) => !next && !update.isPending && onClose()}>
      <DialogContent locked={update.isPending}>
        <DialogHeader>
          <DialogTitle>{copy.changeRoleDialog.title}</DialogTitle>
          <DialogDescription>{copy.changeRoleDialog.description(staff.email)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="change-role-select">{copy.changeRoleDialog.roleLabel}</Label>
          <Select
            value={role}
            onValueChange={(v) => setRole(v as StaffRole)}
            disabled={update.isPending}
          >
            <SelectTrigger id="change-role-select" aria-label={copy.changeRoleDialog.roleLabel}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAFF_ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {staffRoleLabel(r, copy.roleLabels)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">
            {staffRoleDescription(role, copy.roleDescriptions)}
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            {copy.changeRoleDialog.cancel}
          </Button>
          <Button loading={update.isPending} disabled={role === staff.role} onClick={submit}>
            {copy.changeRoleDialog.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
